import React, { useState, useCallback, useMemo } from 'react';
import { useMasterData } from '../../hooks/useMasterData';
import { useAuth } from '../../hooks/useAuth';
import { VehicleModelForm } from '../../components/forms/VehicleModelForm';
import { VehicleModelDetail } from '../../components/vehicle/VehicleModelDetail';
import { VehicleModelFiltersComponent } from '../../components/master/VehicleModelFilters';
import { useVehicleModelFilters } from '../../hooks/useVehicleModelFilters';
import {
  VehiclePowertrain,
  VehicleTag,
  VehicleModelDataSource,
  VehicleModel,
  Role,
} from '../../types';
import {
  TagIcon,
  PlusCircleIcon,
  InformationCircleIcon,
  CogIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { SkeletonCard, SkeletonTable } from '../../components/ui/LoadingSpinner';
import {
  Button,
  PageTitle,
  PaginatedTable,
  PaginatedCards,
  DeleteConfirmModal,
} from '../../components/ui';
import {
  filterVehicleModels,
  sortVehicleModels,
  getPowertrainLabel,
  getPowertrainColorClasses,
  getVehicleTagLabels,
} from '../../utils/vehicleModelUtils';
import logger from '../../utils/logger';
import { BUTTON_TEXTS, MESSAGES } from '../../constants/vehicleModelConstants';

export const VehicleModelListPage: React.FC = () => {
  const { user } = useAuth();
  const {
    vehicleModels,
    vehicleTypes,
    manufacturers,
    addVehicleModel,
    updateVehicleModel,
    deleteVehicleModel,
    loading,
    getFilteredVehicleModels,
    getAllVehicleModelsForAdmin, // ラクハイ管理者専用メソッド追加
  } = useMasterData();

  // 会社設定による車両モデルフィルタリング
  // SUPER_ADMINの場合は全ての車両モデルを表示、それ以外は会社IDでフィルタリング
  const displayedVehicleModels =
    user?.role === Role.SUPER_ADMIN
      ? getAllVehicleModelsForAdmin() // ラクハイ管理者専用：会社フィルタリング無視で全車両モデルを取得
      : user?.companyId
        ? getFilteredVehicleModels(user.companyId)
        : [];

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedVehicleModel, setSelectedVehicleModel] = useState<VehicleModel | null>(null);
  const [editingModel, setEditingModel] = useState<VehicleModel | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    modelId: string;
    modelName: string;
  } | null>(null);

  // フィルター状態管理（カスタムフック使用）
  const filterHooks = useVehicleModelFilters();

  // 新しいVehicleModelFormDataに対応
  interface VehicleModelFormData extends Record<string, unknown> {
    manufacturerId: string;
    vehicleTypeId: string;
    modelName: string;
    powertrain?: VehiclePowertrain;
    vehicleTags?: VehicleTag[];
  }

  const initialFormState: VehicleModelFormData = {
    manufacturerId: '',
    vehicleTypeId: '',
    modelName: '',
    powertrain: undefined,
    vehicleTags: [],
  };
  const [newModelData, setNewModelData] = useState<VehicleModelFormData>(initialFormState);

  // フィルタリング＋ソートされた車両モデル（ユーティリティ関数使用）
  const filteredAndSortedVehicleModels = useMemo(() => {
    // まずフィルタリング
    const filtered = filterVehicleModels(displayedVehicleModels, {
      searchQuery: filterHooks.filters.searchQuery,
      selectedManufacturer: filterHooks.filters.selectedManufacturer,
      selectedVehicleType: filterHooks.filters.selectedVehicleType,
      selectedPowertrain: filterHooks.filters.selectedPowertrain,
      selectedTags: filterHooks.filters.selectedTags,
    });

    // 次にソート
    return sortVehicleModels(
      filtered,
      manufacturers,
      filterHooks.filters.sortKey,
      filterHooks.filters.sortOrder
    );
  }, [displayedVehicleModels, filterHooks.filters, manufacturers]);

  // 統一フィールド変更ハンドラー（VehicleModelForm対応）
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value } = e.target;

      // 新しいフィールド（powertrain, vehicleTags）の型安全な処理
      if (name === 'powertrain') {
        setNewModelData(prev => ({
          ...prev,
          [name]: value === '' ? undefined : (value as VehiclePowertrain),
        }));
      } else if (name === 'vehicleTags') {
        setNewModelData(prev => ({
          ...prev,
          [name]: Array.isArray(value) ? value : [],
        }));
      } else {
        setNewModelData(prev => ({ ...prev, [name]: value }));
      }
    },
    []
  );

  // 車両モデル送信処理（追加・更新の統一ハンドラー）
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (
        !newModelData.manufacturerId ||
        !newModelData.vehicleTypeId ||
        !newModelData.modelName.trim()
      ) {
        alert('メーカー、車種タイプ、モデル名は必須です。');
        return;
      }

      setIsLoading(true);

      try {
        if (editingModel) {
          // 編集モード
          const updatedData = {
            name: newModelData.modelName.trim(),
            manufacturerId: newModelData.manufacturerId,
            vehicleTypeId: newModelData.vehicleTypeId,
            powertrain: newModelData.powertrain,
            vehicleTags: newModelData.vehicleTags || [],
          };

          await updateVehicleModel(editingModel.id, updatedData);
        } else {
          // 新規追加処理（重複チェック）
          if (
            vehicleModels.some(
              model =>
                model.manufacturerId === newModelData.manufacturerId &&
                model.name.toLowerCase() === newModelData.modelName.trim().toLowerCase()
            )
          ) {
            alert('同じメーカーで同じ名前の車両モデルが既に存在します。');
            return;
          }

          // VehicleModel形式でデータを作成
          const vehicleModelData: VehicleModel = {
            id: crypto.randomUUID(),
            name: newModelData.modelName.trim(),
            manufacturerId: newModelData.manufacturerId,
            vehicleTypeId: newModelData.vehicleTypeId,
            powertrain: newModelData.powertrain,
            vehicleTags: newModelData.vehicleTags || [],
            dataSource: VehicleModelDataSource.CUSTOM, // 陸送会社独自データとして作成
          };

          await addVehicleModel(vehicleModelData);
        }

        // フォームリセット - 直接state管理
        setIsFormVisible(false);
        setNewModelData(initialFormState);
        setEditingModel(null);
      } catch (error) {
        logger.error('車両モデルの追加に失敗しました:', error);
        alert('車両モデルの追加に失敗しました。再度お試しください。');
      } finally {
        setIsLoading(false);
      }
    },
    [
      newModelData,
      vehicleModels,
      addVehicleModel,
      updateVehicleModel,
      editingModel,
      initialFormState,
    ]
  );

  // 編集モードをキャンセル
  const cancelEditing = useCallback(() => {
    setNewModelData(initialFormState);
    setIsFormVisible(false);
    setEditingModel(null);
  }, [initialFormState]);

  const handleVehicleModelClick = (vehicleModel: VehicleModel) => {
    setSelectedVehicleModel(vehicleModel);
  };

  const handleCloseDetail = () => {
    setSelectedVehicleModel(null);
  };

  const handleEditVehicleModel = (vehicleModel: VehicleModel) => {
    setEditingModel(vehicleModel);
    setNewModelData({
      manufacturerId: vehicleModel.manufacturerId,
      vehicleTypeId: vehicleModel.vehicleTypeId,
      modelName: vehicleModel.name,
      powertrain: vehicleModel.powertrain || undefined,
      vehicleTags: vehicleModel.vehicleTags || [],
    });
    setSelectedVehicleModel(null); // 詳細モーダルを閉じる
    setIsFormVisible(true); // 編集フォームを開く
  };

  const handleDeleteVehicleModel = (modelId: string, modelName: string) => {
    setDeleteConfirm({ show: true, modelId, modelName });
  };

  const confirmDeleteVehicleModel = async () => {
    if (!deleteConfirm) return;

    try {
      setIsLoading(true);
      await deleteVehicleModel(deleteConfirm.modelId);
      logger.info('✅ 車両モデル削除成功:', deleteConfirm.modelName);
      setDeleteConfirm(null);
      setSelectedVehicleModel(null);
    } catch (error) {
      logger.error('❌ 車両モデル削除失敗:', error);
      alert('車両モデルの削除に失敗しました。もう一度お試しください。');
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div>
        <PageTitle icon={TagIcon} title='車両モデル管理' />
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:hidden'>
          {[...Array(6)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
        <div className='hidden lg:block'>
          <SkeletonTable />
        </div>
      </div>
    );
  }

  const ModelList = () => {
    // ヘルパー関数
    const getManufacturerName = (manufacturerId: string) => {
      const manufacturer = manufacturers.find(m => m.id === manufacturerId);
      return manufacturer?.name || manufacturerId;
    };

    const getVehicleTypeName = (vehicleTypeId: string) => {
      const vehicleType = vehicleTypes.find(vt => vt.id === vehicleTypeId);
      return vehicleType?.name || vehicleTypeId;
    };

    return (
      <div>
        {/* システム設定案内メッセージ */}
        {user?.companyId && (
          <div className='mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg'>
            <div className='flex items-start space-x-3'>
              <InformationCircleIcon className='h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0' />
              <div className='flex-1'>
                <h3 className='text-sm font-medium text-blue-800 dark:text-blue-200 mb-1'>
                  車両モデルの表示設定について
                </h3>
                <p className='text-sm text-blue-700 dark:text-blue-300 mb-2'>
                  利用する車両モデルはシステム設定から設定できます。設定で有効化された車両モデルのみがこのページに表示されます。
                </p>
                <a
                  href='/admin/settings'
                  className='inline-flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline'
                >
                  <CogIcon className='h-4 w-4 mr-1' />
                  システム設定で車両モデルを管理
                </a>
              </div>
            </div>
          </div>
        )}

        {/* フィルターコンポーネント */}
        <VehicleModelFiltersComponent
          manufacturers={manufacturers}
          vehicleTypes={vehicleTypes}
          filterHooks={filterHooks}
        />

        {/* 結果表示 */}
        <div className='mb-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3'>
          <div className='flex flex-col sm:flex-row sm:items-center gap-2'>
            <span className='text-sm text-gray-600 dark:text-gray-400'>
              {MESSAGES.RESULTS_COUNT(filteredAndSortedVehicleModels.length)}
            </span>
            {filterHooks.hasActiveFilters && (
              <div className='flex items-center gap-2'>
                <span className='text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded-full'>
                  フィルター適用中
                </span>
                <button
                  onClick={filterHooks.resetFilters}
                  className='text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 underline'
                >
                  すべてクリア
                </button>
              </div>
            )}
          </div>
          <Button
            onClick={() => setIsFormVisible(true)}
            className='flex items-center gap-2'
            variant='primary'
          >
            <PlusCircleIcon className='w-5 h-5' />
            {BUTTON_TEXTS.NEW_VEHICLE_MODEL}
          </Button>
        </div>

        {/* データが空の場合 */}
        {filteredAndSortedVehicleModels.length === 0 ? (
          <div className='text-center py-12'>
            <TagIcon className='mx-auto h-12 w-12 text-gray-400' />
            <h3 className='mt-2 text-sm font-medium text-gray-900 dark:text-white'>
              {MESSAGES.NO_RESULTS}
            </h3>
          </div>
        ) : (
          <div>
            {/* デスクトップ表示：テーブル */}
            <div className='hidden lg:block'>
              <PaginatedTable
                data={filteredAndSortedVehicleModels}
                loading={loading}
                storageKey='vehicleModelList'
                onRowClick={handleVehicleModelClick}
                getRowTestId={(record: VehicleModel) => `vehicle-model-row-${record.id}`}
                columns={[
                  {
                    key: 'vehicleType',
                    label: '車種タイプ',
                    render: (record: VehicleModel, _index: number) => (
                      <span className='text-sm text-gray-900 dark:text-white'>
                        {getVehicleTypeName(record.vehicleTypeId)}
                      </span>
                    ),
                  },
                  {
                    key: 'manufacturer',
                    label: 'メーカー',
                    render: (record: VehicleModel, _index: number) => (
                      <span className='text-sm text-gray-900 dark:text-white'>
                        {getManufacturerName(record.manufacturerId)}
                      </span>
                    ),
                  },
                  {
                    key: 'modelName',
                    label: 'モデル名',
                    render: (record: VehicleModel, _index: number) => (
                      <div className='flex items-center gap-2'>
                        <span className='text-sm font-medium text-gray-900 dark:text-white'>
                          {record.name}
                        </span>
                        {record.dataSource === 'custom' && (
                          <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'>
                            <PlusCircleIcon className='w-3 h-3 mr-1' />
                            独自登録
                          </span>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: 'specifications',
                    label: '仕様',
                    render: (record: VehicleModel, _index: number) => (
                      <div className='space-y-1'>
                        {/* パワートレイン */}
                        {record.powertrain && (
                          <div>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPowertrainColorClasses(record.powertrain)}`}
                            >
                              {getPowertrainLabel(record.powertrain)}
                            </span>
                          </div>
                        )}

                        {/* タグ */}
                        {record.vehicleTags && record.vehicleTags.length > 0 && (
                          <div className='flex flex-wrap gap-1'>
                            {getVehicleTagLabels(record.vehicleTags).map((tag, tagIndex) => (
                              <span
                                key={tagIndex}
                                className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* 両方ない場合 */}
                        {!record.powertrain &&
                          (!record.vehicleTags || record.vehicleTags.length === 0) && (
                            <span className='text-gray-500 dark:text-gray-400'>-</span>
                          )}
                      </div>
                    ),
                  },
                ]}
                rowKey='id'
                emptyMessage='車両モデルがありません'
              />
            </div>

            {/* モバイル表示：カード */}
            <div className='lg:hidden'>
              <PaginatedCards
                data={filteredAndSortedVehicleModels}
                loading={loading}
                storageKey='vehicleModelList'
                keyExtractor='id'
                emptyMessage='車両モデルがありません'
                renderCard={model => (
                  <div
                    data-testid={`vehicle-model-card-${model.id}`}
                    onClick={() => handleVehicleModelClick(model)}
                    className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 hover:shadow-lg transition-all border-2 border-transparent hover:border-blue-200 dark:hover:border-blue-800 cursor-pointer'
                  >
                    <div className='flex justify-between items-start mb-2'>
                      <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                        {model.name}
                      </h3>
                    </div>
                    <div className='space-y-1 text-sm text-gray-600 dark:text-gray-300 mb-3'>
                      <div>メーカー: {getManufacturerName(model.manufacturerId)}</div>
                      <div>車種: {getVehicleTypeName(model.vehicleTypeId)}</div>
                      {model.powertrain && (
                        <div className='flex items-center gap-2'>
                          <span>パワートレイン:</span>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPowertrainColorClasses(model.powertrain)}`}
                          >
                            {getPowertrainLabel(model.powertrain)}
                          </span>
                        </div>
                      )}
                      {model.vehicleTags && model.vehicleTags.length > 0 && (
                        <div>タグ: {model.vehicleTags.join(', ')}</div>
                      )}
                    </div>
                    <div className='flex justify-end pt-2 border-t border-gray-200 dark:border-gray-700'>
                      <button
                        onClick={() => handleVehicleModelClick(model)}
                        className='inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50'
                      >
                        <EyeIcon className='h-4 w-4' />
                        詳細を見る
                      </button>
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
        )}

        {filteredAndSortedVehicleModels.length > 0 && (
          <div className='mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4'>
            <div className='flex items-start'>
              <TagIcon className='h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5' />
              <div>
                <h3 className='text-sm font-medium text-blue-800 dark:text-blue-200 mb-1'>
                  メーカー別管理について
                </h3>
                <p className='text-sm text-blue-700 dark:text-blue-300'>
                  車両モデルはメーカーごとに整理されています。
                  新規登録時はメーカー、車種タイプ、モデル名を選択・入力してください。
                  データの正規化により、より効率的な管理が可能になりました。
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <PageTitle icon={TagIcon} title='車両モデル管理' />

      {isFormVisible ? (
        <VehicleModelForm
          formData={newModelData}
          editingModel={
            editingModel
              ? {
                  id: editingModel.id,
                  name: editingModel.name,
                  manufacturerId: editingModel.manufacturerId,
                  vehicleTypeId: editingModel.vehicleTypeId,
                }
              : null
          }
          manufacturers={manufacturers}
          vehicleTypes={vehicleTypes}
          isLoading={isLoading}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          onCancel={cancelEditing}
        />
      ) : (
        <ModelList />
      )}

      {/* 車両モデル詳細モーダル */}
      {selectedVehicleModel && (
        <VehicleModelDetail
          vehicleModel={selectedVehicleModel}
          onClose={handleCloseDetail}
          onEdit={handleEditVehicleModel}
          onDelete={user?.role === Role.SUPER_ADMIN ? handleDeleteVehicleModel : undefined}
          isModal={true}
        />
      )}

      {/* 削除確認モーダル */}
      <DeleteConfirmModal
        isOpen={deleteConfirm?.show || false}
        itemName={deleteConfirm?.modelName || ''}
        itemType='車両モデル'
        onConfirm={confirmDeleteVehicleModel}
        onCancel={() => setDeleteConfirm(null)}
        isDeleting={isLoading}
      />
    </div>
  );
};

export default VehicleModelListPage;
